import ShieldAlertIcon from "@redotech/redo-icons/alert-circle.svg";
import ArchiveIcon from "@redotech/redo-icons/archive.svg";
import CreditCardIcon from "@redotech/redo-icons/credit-card-01.svg";
import MailIcon from "@redotech/redo-icons/mail-01.svg";
import BotMessageSquareIcon from "@redotech/redo-icons/message-chat-circle.svg";
import PenLineIcon from "@redotech/redo-icons/pencil-01.svg";
import RadioIcon from "@redotech/redo-icons/radio.svg";
import RotateCcwIcon from "@redotech/redo-icons/reverse-left_filled.svg";
import BrainCircuitIcon from "@redotech/redo-icons/stars-01.svg";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import { classifyReturnsRouting } from "@redotech/redo-onboarding-api/returns-routing";
import type { ProductId } from "@redotech/redo-onboarding-api/schema/initialize";
import type { JSXElementConstructor } from "react";
import { getFormData, getProductConfig } from "./contexts/product-config-store";

declare const process: { env: Record<string, string | undefined> };

const S3_BASE =
  process.env.REDO_STATIC_ASSETS_URL ??
  "https://redoapi-dev.s3.amazonaws.com/redo/static/onboarding-v2";

type IconComponent = JSXElementConstructor<any>;

export interface ProductStep {
  id: string;
  label: string;
  path: string;
  shouldSkip?: () => boolean;
  hideFromNav?: boolean;
  navGroup?: string;
  resumeEligible?: boolean;
  returnToStepId?: string;
}

export interface ProductSetupHighlight {
  label: string;
  description?: string;
}

export interface ProductEntry {
  id: ProductId;
  label: string;
  description: string;
  subtext: string;
  /** When true the landing page offers the product as a self-serve setup. */
  selfServe?: boolean;
  /** Landing-page "Setup will cover" bullets. Defaults to the first 4 nav groups. */
  setupHighlights?: ProductSetupHighlight[];
  imageUrl: string;
  defaultRoute: string;
  /** When set, clicking this product on the landing page redirects to this path in the merchant app instead of opening the onboarding flow. */
  externalRedirectPath?: string;
  icon?: IconComponent;
  steps: ProductStep[];
}

export interface ProductFeatureFlags {
  omsOnboarding?: boolean;
  contextualOnboarding?: boolean;
}

const SCHEDULE_CALL_STEP: ProductStep = {
  id: "schedule-call",
  label: "Schedule a call",
  path: "schedule-call",
};

/**
 * True when the current merchant should be routed to the human-handoff path
 * (schedule-call) instead of self-serve go-live. See `classifyReturnsRouting`.
 */
export function isLargeReturnsMerchant(): boolean {
  return (
    classifyReturnsRouting(
      getProductConfig().dealSizing?.totalAnnualOrderVolume ?? null,
      getProductConfig().dealSizing?.provisionalOrderCount30d ?? null,
    ).decision === "call"
  );
}

/**
 * True when the merchant is known to be off Shopify. The Shopify-only embed
 * steps cannot run for them, so they hand off to a call instead. Unknown
 * platform is treated as Shopify so existing flows are unchanged.
 */
export function isNonShopifyMerchant(): boolean {
  return getProductConfig().returns?.isShopify === false;
}

function shouldHandOffReturnsToCall(): boolean {
  return isLargeReturnsMerchant() || isNonShopifyMerchant();
}

export const PRODUCTS: ProductEntry[] = [
  {
    id: "returns",
    label: "Returns",
    description:
      "We'll walk you through creating an automated Returns Portal that personalizes every experience, turning refunds into exchanges and claims into opportunities to build trust. Then we'll set up Checkout+, our signature returns label coverage product. Your customers can opt in to coverage for a low fee, and we'll cover the cost of return labels. Portal and Checkout+ are absolutely free for you",
    subtext: "15 minute set up",
    selfServe: true,
    setupHighlights: [
      {
        label: "Returns Policy",
        description:
          "Import your existing policy to automatically configure return window, eligible items, and exchange/refund rules",
      },
      {
        label: "Communications",
        description: "Configure emails your customers receive during a return.",
      },
      {
        label: "Customer Experience",
        description:
          "Customize our Returns Portal to match your brand's design language exactly",
      },
      {
        label: "Checkout+",
        description:
          "Configure how your customers can opt into returns coverage",
      },
    ],
    icon: RotateCcwIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-returns.webp`,
    defaultRoute: "return-policy",
    steps: [
      {
        id: "ret-return-policy",
        label: "Return Policy",
        path: "return-policy",
        navGroup: "Policy",
      },
      {
        id: "ret-processing-rules",
        label: "Return Processing",
        path: "processing-rules",
        navGroup: "Policy",
      },
      {
        id: "ret-communications",
        label: "Communications",
        path: "communications",
        navGroup: "Communications",
      },
      {
        id: "ret-portal-preview",
        label: "Return Portal Preview",
        path: "portal-preview",
        navGroup: "Experience",
        shouldSkip: isLargeReturnsMerchant,
      },
      // Must precede the coverage steps: the coverage widget preview renders the
      // storefront extension, which only exists once the embed is enabled.
      {
        id: "ret-app-embed",
        label: "App Embed",
        path: "app-embed",
        navGroup: "Coverage",
        shouldSkip: shouldHandOffReturnsToCall,
      },
      {
        id: "ret-coverage-widget",
        label: "Checkout+",
        path: "coverage-widget",
        navGroup: "Coverage",
        shouldSkip: shouldHandOffReturnsToCall,
      },
      {
        id: "ret-billing",
        label: "Billing",
        path: "billing",
        navGroup: "Launch",
        shouldSkip: isLargeReturnsMerchant,
      },
      {
        id: "ret-go-live",
        label: "Go Live",
        path: "go-live",
        navGroup: "Launch",
        shouldSkip: shouldHandOffReturnsToCall,
      },
      {
        id: "ret-schedule-call",
        label: "Schedule a call",
        path: "schedule-call",
        navGroup: "Launch",
        shouldSkip: () => !shouldHandOffReturnsToCall(),
      },
    ],
  },
  {
    id: "order-editing",
    label: "Order Editing",
    description:
      "Let customers update eligible orders before they reach fulfillment.",
    subtext: "10 minute set up",
    selfServe: true,
    icon: PenLineIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-order-editing.webp`,
    defaultRoute: "overview",
    steps: [
      {
        id: "oe-overview",
        label: "Overview",
        path: "overview",
        hideFromNav: true,
      },
      { id: "oe-how-it-works", label: "How It Works", path: "video-intro" },
      {
        id: "oe-customer-actions",
        label: "Customer Actions",
        path: "enabled-actions",
      },
      { id: "oe-timing", label: "Timing", path: "time-window" },
      {
        id: "oe-platforms",
        label: "Platforms",
        path: "platforms",
        shouldSkip: () => {
          const config = getProductConfig().orderEditing;
          if (!config) {
            return true;
          }
          return (
            !config.shopperAI &&
            !config.supportSelfServe &&
            !config.orderTracking
          );
        },
      },
      {
        id: "oe-widget-preview",
        label: "Widget Preview",
        path: "widget-preview",
      },
      { id: "oe-activate", label: "Activate", path: "app-embed" },
      {
        id: "oe-completion",
        label: "Complete",
        path: "completion",
        hideFromNav: true,
      },
    ],
  },
  {
    id: "shipping-fulfillment",
    label: "Shipping & Fulfillment",
    description:
      "Set up the channels, carriers, packaging, and automations that power fulfillment.",
    subtext: "20 minute set up",
    selfServe: true,
    icon: TruckIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-shipping-fulfillment.webp`,
    defaultRoute: "overview",
    steps: [
      {
        id: "sf-overview",
        label: "Overview",
        path: "overview",
        hideFromNav: true,
      },
      {
        id: "sf-current-software",
        label: "Current Software",
        path: "current-software",
        navGroup: "Setup",
      },
      {
        id: "sf-channels",
        label: "Sales Channels",
        path: "channels",
        navGroup: "Setup",
      },
      {
        id: "sf-carriers",
        label: "Carriers",
        path: "carriers",
        navGroup: "Operations",
      },
      {
        id: "sf-packaging",
        label: "Packaging",
        path: "packaging",
        navGroup: "Operations",
      },
      {
        id: "sf-packing-slip",
        label: "Packing Slip",
        path: "packing-slip",
        navGroup: "Operations",
      },
      {
        id: "sf-shipping-automations",
        label: "Automations",
        path: "shipping-automations",
        navGroup: "Operations",
      },
      {
        id: "sf-order-automations",
        label: "Order Automations",
        path: "order-automations",
        navGroup: "Operations",
      },
      {
        id: "sf-international",
        label: "International",
        path: "international",
        navGroup: "Operations",
        shouldSkip: () =>
          getProductConfig().shippingFulfillment?.skipInternational === true,
      },
      {
        id: "sf-views",
        label: "Views",
        path: "views",
        navGroup: "Finalization",
      },
      {
        id: "sf-billing",
        label: "Billing",
        path: "billing",
        navGroup: "Finalization",
        shouldSkip: () =>
          getProductConfig().shippingFulfillment?.skipBilling === true,
      },
      {
        id: "sf-pain-points",
        label: "Feedback",
        path: "pain-points",
        navGroup: "Finalization",
      },
      {
        id: "sf-completion",
        label: "Complete",
        path: "completion",
        hideFromNav: true,
      },
    ],
  },
  {
    id: "chargebacks",
    label: "Chargebacks",
    description:
      "Connect dispute sources and automate the evidence used to recover chargebacks.",
    subtext: "5 minute set up",
    selfServe: true,
    icon: ShieldAlertIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-chargebacks.webp`,
    defaultRoute: "overview",
    steps: [
      {
        id: "cb-overview",
        label: "Overview",
        path: "overview",
        hideFromNav: true,
      },
      {
        id: "cb-how-it-works",
        label: "How It Works",
        path: "how-it-works",
        navGroup: "Overview",
      },
      {
        id: "cb-permissions",
        label: "Permissions",
        path: "permissions",
        navGroup: "Overview",
        shouldSkip: () =>
          getProductConfig().chargebacks?.hasDisputeScopes === true,
      },
      {
        id: "cb-dispute-sources",
        label: "Dispute Sources",
        path: "dispute-sources",
        navGroup: "Setup",
      },
      {
        id: "cb-go-live",
        label: "Go Live",
        path: "go-live",
        navGroup: "Launch",
      },
      {
        id: "cb-completion",
        label: "Complete",
        path: "completion",
        hideFromNav: true,
      },
    ],
  },
  {
    id: "withdrawals",
    label: "Withdrawals",
    description:
      "Configure and manage withdrawal requests alongside your returns operations.",
    subtext: "5 minute set up",
    selfServe: true,
    icon: RotateCcwIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-returns.webp`,
    defaultRoute: SCHEDULE_CALL_STEP.path,
    externalRedirectPath: "/returns/withdrawals/all-withdrawals?setup=true",
    steps: [SCHEDULE_CALL_STEP],
  },
  {
    id: "contextual",
    label: "Contextual",
    description:
      "Connect first-party storefront events to the marketing destinations you already use.",
    subtext: "15 minute set up",
    selfServe: true,
    icon: RadioIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-contextual.webp`,
    defaultRoute: "overview",
    steps: [
      {
        id: "ctx-overview",
        label: "Overview",
        path: "overview",
        hideFromNav: true,
      },
      {
        id: "ctx-subdomain",
        label: "Subdomain Setup",
        path: "subdomain",
        navGroup: "Setup",
      },
      {
        id: "ctx-tag-setup",
        label: "Tag Setup",
        path: "tag-setup",
        navGroup: "Setup",
        shouldSkip: () => {
          const fd = getFormData();
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const source = (fd["ctx-subdomain"] as any)?.merchantSource;
          const dests: string[] =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            (fd["ctx-destinations"] as any)?.selectedDestinations ?? [];
          const AD_PLATFORMS = ["meta", "tiktok", "snapchat"];
          return (
            source === "other" && !dests.some((d) => AD_PLATFORMS.includes(d))
          );
        },
      },
      {
        id: "ctx-destinations",
        label: "Destinations",
        path: "destinations",
        navGroup: "Integrations",
      },
      {
        id: "ctx-email-sms",
        label: "Email & SMS",
        path: "email-sms",
        navGroup: "Integrations",
        shouldSkip: () => {
          const dests: string[] =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            (getFormData()["ctx-destinations"] as any)?.selectedDestinations ??
            [];
          const EMAIL_SMS = ["klaviyo", "attentive", "postscript"];
          return !dests.some((d) => EMAIL_SMS.includes(d));
        },
      },
      {
        id: "ctx-ad-platforms",
        label: "Ad Platforms",
        path: "ad-platforms",
        navGroup: "Integrations",
        shouldSkip: () => {
          const dests: string[] =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            (getFormData()["ctx-destinations"] as any)?.selectedDestinations ??
            [];
          const AD_PLATFORMS = ["meta", "tiktok", "snapchat"];
          return !dests.some((d) => AD_PLATFORMS.includes(d));
        },
      },
      {
        id: "ctx-completion",
        label: "Complete",
        path: "completion",
        navGroup: "Launch",
      },
    ],
  },
  {
    id: "email-sms",
    label: "Email & SMS",
    description:
      "Plan branded email and SMS experiences with the Redo marketing team.",
    subtext: "Schedule a call",
    icon: MailIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-email-sms.webp`,
    defaultRoute: "schedule-call",
    steps: [
      { id: "schedule-call", label: "Schedule a call", path: "schedule-call" },
    ],
  },
  {
    id: "aeo",
    label: "Answer Engine Optimization",
    description:
      "Improve how answer engines discover, understand, and describe your brand.",
    subtext: "Schedule a call",
    icon: BrainCircuitIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-aeo.webp`,
    defaultRoute: "schedule-call",
    steps: [
      { id: "schedule-call", label: "Schedule a call", path: "schedule-call" },
    ],
  },
  {
    id: "checkout-optimization",
    label: "Checkout Optimization",
    description:
      "Design checkout experiences that increase conversion and average order value.",
    subtext: "Schedule a call",
    icon: CreditCardIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-checkout-optimization.webp`,
    defaultRoute: "schedule-call",
    steps: [
      { id: "schedule-call", label: "Schedule a call", path: "schedule-call" },
    ],
  },
  {
    id: "ai-sales-support",
    label: "AI Sales + Support",
    description:
      "Create AI-powered shopping and support experiences grounded in your brand.",
    subtext: "Schedule a call",
    icon: BotMessageSquareIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-ai-sales-support.webp`,
    defaultRoute: "schedule-call",
    steps: [
      { id: "schedule-call", label: "Schedule a call", path: "schedule-call" },
    ],
  },
  {
    id: "inventory-management",
    label: "Inventory Management",
    description:
      "Plan inventory visibility and operations across your locations and sales channels.",
    subtext: "Schedule a call",
    icon: ArchiveIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-inventory-management.webp`,
    defaultRoute: "schedule-call",
    steps: [
      { id: "schedule-call", label: "Schedule a call", path: "schedule-call" },
    ],
  },
];

export const PRODUCTS_BY_ID = new Map(PRODUCTS.map((p) => [p.id, p]));

function downgradeToCallOnly(product: ProductEntry): ProductEntry {
  return {
    ...product,
    subtext: "Schedule a call",
    selfServe: false,
    defaultRoute: SCHEDULE_CALL_STEP.path,
    steps: [SCHEDULE_CALL_STEP],
  };
}

export function getProductsForFeatureFlags(
  featureFlags?: ProductFeatureFlags | null,
): ProductEntry[] {
  return PRODUCTS.map((product) => {
    if (
      product.id === "shipping-fulfillment" &&
      featureFlags?.omsOnboarding === false
    ) {
      return downgradeToCallOnly(product);
    }
    if (product.id === "chargebacks") {
      return {
        ...product,
        subtext: "10 minute set up",
        selfServe: true,
        externalRedirectPath:
          "/payments/chargebacks/overview?setupSource=setup_app",
        defaultRoute: SCHEDULE_CALL_STEP.path,
        steps: [SCHEDULE_CALL_STEP],
      };
    }
    if (product.id === "order-editing") {
      return {
        ...product,
        subtext: "10 minute set up",
        selfServe: true,
        externalRedirectPath: "/welcome/order-editing",
        defaultRoute: SCHEDULE_CALL_STEP.path,
        steps: [SCHEDULE_CALL_STEP],
      };
    }
    if (
      product.id === "contextual" &&
      featureFlags?.contextualOnboarding === false
    ) {
      return downgradeToCallOnly(product);
    }
    return product;
  });
}
